import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, Loader2, Wand2, Play, Info, RefreshCw, Copy, CheckCircle, Download, Terminal, FileText, Image, XCircle, Save, X, ChevronDown, ChevronRight } from 'lucide-react';
import { useClerk, useOrganization } from '@clerk/clerk-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './ReportEditorPage.css';

function ReportEditorPage({ reportId, reportName, selectedWorkspace, onClose }) {
  const [messages, setMessages] = useState([]);
  const [instruction, setInstruction] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Edit session state
  const [editSessionInitialized, setEditSessionInitialized] = useState(false);
  const [editSessionLoading, setEditSessionLoading] = useState(false);

  // Explanation state
  const [reportExplanation, setReportExplanation] = useState('');
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationGenerated, setExplanationGenerated] = useState(false);

  // Output state
  const [reportOutput, setReportOutput] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('explanation'); // Default to explanation tab
  const [copiedStates, setCopiedStates] = useState({});

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  // Selected outputs for context
  const [selectedOutputs, setSelectedOutputs] = useState([]);

  // Expanded edit items
  const [expandedEdits, setExpandedEdits] = useState({});

  const messagesEndRef = useRef(null);
  const clerk = useClerk();
  const { organization } = useOrganization();

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize edit session when page opens
  useEffect(() => {
    if (!reportId || !selectedWorkspace?.workspace_id || !clerk?.session || !organization) {
      return;
    }

    const initEditSession = async () => {
      setEditSessionLoading(true);
      try {
        const sessionToken = await clerk.session.getToken();
        const orgSlug = getOrgSlug(organization);
        const apiBaseUrl = getApiBaseUrl(orgSlug);
        const queryParams = new URLSearchParams({
          workspace_id: selectedWorkspace.workspace_id
        });

        const response = await fetch(
          `${apiBaseUrl}/saved-reports/${reportId}/edit/init?${queryParams}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setEditSessionInitialized(true);
            console.log('Edit session initialized:', data.edit_dir);
          }
        } else {
          console.error('Failed to initialize edit session');
        }
      } catch (error) {
        console.error('Error initializing edit session:', error);
      } finally {
        setEditSessionLoading(false);
      }
    };

    initEditSession();
  }, [reportId, selectedWorkspace?.workspace_id, clerk?.session, organization]);

  // Function to load explanation from edit folder
  const loadExplanation = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id) {
      return;
    }

    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/edit/explanation?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.has_explanation && data.explanation) {
          setReportExplanation(data.explanation);
          setExplanationGenerated(true);
        }
      }
    } catch (error) {
      console.error('Error loading explanation:', error);
    }
  };

  // Load existing explanation from edit folder after edit session is initialized
  useEffect(() => {
    if (!reportId || !selectedWorkspace?.workspace_id || !clerk?.session || !organization || !editSessionInitialized) {
      return;
    }

    loadExplanation();
  }, [reportId, selectedWorkspace?.workspace_id, clerk?.session, organization, editSessionInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-run report when edit session is initialized
  useEffect(() => {
    if (editSessionInitialized && !isRunning && !reportOutput) {
      handleRunReport();
    }
  }, [editSessionInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset state when report changes
  useEffect(() => {
    setReportOutput(null);
    setReportExplanation('');
    setExplanationGenerated(false);
    setExplanationLoading(false);
    setMessages([]);
    setActiveTab('explanation');
    setEditSessionInitialized(false);
    setSelectedOutputs([]);
    setExpandedEdits({});
  }, [reportId]);

  const handleRunReport = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      return;
    }

    if (!editSessionInitialized) {
      console.error('Edit session not initialized');
      return;
    }

    setIsRunning(true);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/edit/run?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success && data.outputs) {
        setReportOutput({
          outputs: data.outputs,
          success: data.success
        });
        // Switch to first output tab after running
        const tabs = getOutputTabs(data.outputs);
        if (tabs.length > 0) {
          setActiveTab(tabs[0].id);
        }
      } else if (data.execution_result?.outputs) {
        setReportOutput({
          outputs: data.execution_result.outputs,
          success: data.success
        });
        const tabs = getOutputTabs(data.execution_result.outputs);
        if (tabs.length > 0) {
          setActiveTab(tabs[0].id);
        }
      } else {
        console.error('Failed to run report:', data.detail || data.message);
      }
    } catch (error) {
      console.error('Error running report:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const handleSaveChanges = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !editSessionInitialized) {
      return;
    }

    setIsSaving(true);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/edit/save?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setEditSessionInitialized(false);
        onClose();
      } else {
        console.error('Failed to save changes:', data.detail || data.message);
      }
    } catch (error) {
      console.error('Error saving changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateExplanation = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId || !editSessionInitialized) {
      return;
    }

    setExplanationLoading(true);
    setExplanationGenerated(false);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/edit/explanation/generate?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.explanation) {
          setReportExplanation(data.explanation);
          setExplanationGenerated(true);
        } else {
          setReportExplanation('Unable to generate explanation for this report.');
          setExplanationGenerated(true);
        }
      } else {
        setReportExplanation('Error generating explanation. Please try again later.');
        setExplanationGenerated(true);
      }
    } catch (error) {
      console.error('Error generating report explanation:', error);
      setReportExplanation('Error generating explanation. Please try again later.');
      setExplanationGenerated(true);
    } finally {
      setExplanationLoading(false);
    }
  };

  const handleCopy = async (content, key) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownload = (content, filename) => {
    if (isImageFile(filename)) {
      let downloadUrl;
      if (content.startsWith('data:')) {
        downloadUrl = content;
      } else {
        const ext = filename.split('.').pop()?.toLowerCase();
        downloadUrl = `data:image/${ext};base64,${content}`;
      }

      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(ext);
  };

  const isHtmlFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'html' || ext === 'htm';
  };

  const isJsonFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'json';
  };

  const isMarkdownFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'md' || ext === 'markdown';
  };

  const isCSVFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'csv';
  };

  const formatJsonContent = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      return jsonString;
    }
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const rows = lines.slice(1).map(line =>
      line.split(',').map(cell => cell.trim().replace(/"/g, ''))
    );

    return { headers, rows };
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FileText size={16} />;

    if (isImageFile(filename)) {
      return <Image size={16} />;
    }

    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'json':
      case 'csv':
      case 'txt':
      case 'log':
        return <FileText size={16} />;
      case 'py':
      case 'js':
      case 'html':
      case 'css':
        return <Terminal size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  const getSyntaxClass = (filename) => {
    if (!filename) return 'language-text';
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py':
        return 'language-python';
      case 'js':
        return 'language-javascript';
      case 'json':
        return 'language-json';
      case 'html':
        return 'language-html';
      case 'css':
        return 'language-css';
      case 'csv':
        return 'language-csv';
      case 'md':
      case 'markdown':
        return 'language-markdown';
      default:
        return 'language-text';
    }
  };

  const getStdoutContent = () => {
    if (reportOutput?.outputs) {
      const stdoutFile = reportOutput.outputs.find(o => o.filename === 'stdout.log');
      if (stdoutFile) return stdoutFile.content;
    }
    return null;
  };

  const getErrorContent = () => {
    if (reportOutput?.outputs) {
      const errorFile = reportOutput.outputs.find(o => o.filename === 'error.log');
      if (errorFile) return errorFile.content;
    }
    return null;
  };

  // Get output tabs (without explanation - that's always first)
  const getOutputTabs = (outputs) => {
    const tabs = [];
    if (!outputs) return tabs;

    const stdoutFile = outputs.find(o => o.filename === 'stdout.log');
    const errorFile = outputs.find(o => o.filename === 'error.log');
    const outputFiles = outputs.filter(o => o.filename !== 'error.log' && o.filename !== 'stdout.log');

    if (stdoutFile?.content) {
      tabs.push({ id: 'stdout', label: 'Output', icon: <Terminal size={16} /> });
    }

    if (errorFile?.content) {
      tabs.push({ id: 'stderr', label: 'Errors', icon: <XCircle size={16} /> });
    }

    outputFiles.forEach((output, index) => {
      tabs.push({
        id: `file-${index}`,
        label: output.filename,
        icon: getFileIcon(output.filename),
        output: output
      });
    });

    return tabs;
  };

  const renderFileContent = (output) => {
    if (!output.content) return null;

    if (isImageFile(output.filename)) {
      return (
        <div className="image-preview">
          <img
            src={output.content.startsWith('data:') ? output.content : `data:image/${output.filename.split('.').pop()?.toLowerCase()};base64,${output.content}`}
            alt={output.filename}
            className="output-image"
          />
        </div>
      );
    } else if (isHtmlFile(output.filename)) {
      return (
        <div className="html-preview">
          <iframe
            srcDoc={output.content}
            className="html-iframe"
            title={output.filename}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      );
    } else if (isMarkdownFile(output.filename)) {
      return (
        <div className="markdown-preview">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {output.content}
          </ReactMarkdown>
        </div>
      );
    } else if (isJsonFile(output.filename)) {
      return (
        <pre className={`file-content ${getSyntaxClass(output.filename)}`}>
          {formatJsonContent(output.content)}
        </pre>
      );
    } else if (isCSVFile(output.filename)) {
      return (
        <div className="csv-content">
          <div className="csv-table-container">
            {(() => {
              const { headers, rows } = parseCSV(output.content);
              return (
                <table className="csv-table">
                  <thead>
                    <tr>
                      {headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      );
    } else {
      return (
        <pre className={`file-content ${getSyntaxClass(output.filename)}`}>
          {output.content}
        </pre>
      );
    }
  };

  const renderLeftPanelContent = () => {
    // Explanation tab
    if (activeTab === 'explanation') {
      return (
        <div className="tab-panel-content">
          {explanationLoading ? (
            <div className="explanation-loading">
              <Loader2 size={24} className="spin" />
              <p>Generating explanation...</p>
              <span className="loading-hint">This may take a moment...</span>
            </div>
          ) : explanationGenerated && reportExplanation ? (
            <div className="explanation-markdown">
              <div className="content-actions">
                <button
                  className="icon-button"
                  onClick={handleGenerateExplanation}
                  disabled={explanationLoading}
                  title="Regenerate explanation"
                >
                  <RefreshCw size={14} />
                </button>
                <button
                  className="icon-button"
                  onClick={() => handleCopy(reportExplanation, 'explanation')}
                  title="Copy explanation"
                >
                  {copiedStates.explanation ? <CheckCircle size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {reportExplanation}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="explanation-prompt">
              <Info size={48} />
              <h3>How Does This Report Work?</h3>
              <p>
                Get an AI-generated explanation of how this report processes data, including:
              </p>
              <ul>
                <li>What data fields, tables, and sources are accessed</li>
                <li>What calculations, filters, and transformations are applied</li>
                <li>How the data flows through the report</li>
              </ul>
              <button
                className="generate-button"
                onClick={handleGenerateExplanation}
                disabled={explanationLoading || !editSessionInitialized}
              >
                <Info size={16} />
                <span>Generate Explanation</span>
              </button>
              <p className="explanation-note">
                Note: Generating an explanation will use AI credits
              </p>
            </div>
          )}
        </div>
      );
    }

    // Stdout tab
    if (activeTab === 'stdout') {
      const stdoutContent = getStdoutContent();
      return (
        <div className="tab-panel-content">
          <div className="content-actions">
            <button
              className="action-button"
              onClick={() => handleCopy(stdoutContent, 'stdout')}
              title="Copy output"
            >
              {copiedStates.stdout ? <CheckCircle size={14} /> : <Copy size={14} />}
              <span>Copy</span>
            </button>
            <button
              className="action-button"
              onClick={() => handleDownload(stdoutContent, `${reportName}_output.txt`)}
              title="Download output"
            >
              <Download size={14} />
              <span>Download</span>
            </button>
          </div>
          <pre className="execution-output">
            {stdoutContent}
          </pre>
        </div>
      );
    }

    // Stderr tab
    if (activeTab === 'stderr') {
      const errorContent = getErrorContent();
      return (
        <div className="tab-panel-content">
          <div className="content-actions">
            <button
              className="action-button"
              onClick={() => handleCopy(errorContent, 'stderr')}
              title="Copy errors"
            >
              {copiedStates.stderr ? <CheckCircle size={14} /> : <Copy size={14} />}
              <span>Copy</span>
            </button>
            <button
              className="action-button"
              onClick={() => handleDownload(errorContent, `${reportName}_errors.txt`)}
              title="Download errors"
            >
              <Download size={14} />
              <span>Download</span>
            </button>
          </div>
          <pre className="execution-output error-output">
            {errorContent}
          </pre>
        </div>
      );
    }

    // File output tabs
    const fileIndex = parseInt(activeTab.replace('file-', ''));
    if (!isNaN(fileIndex)) {
      const outputFiles = reportOutput?.outputs?.filter(o => o.filename !== 'error.log' && o.filename !== 'stdout.log') ?? [];
      const output = outputFiles[fileIndex];
      if (output) {
        return (
          <div className="tab-panel-content">
            <div className="content-actions">
              <div className="file-info">
                {getFileIcon(output.filename)}
                <span className="file-name">{output.filename}</span>
                <span className="file-size">
                  {output.content ? `${output.content.length} chars` : 'Empty'}
                </span>
              </div>
              <button
                className="action-button"
                onClick={() => handleCopy(output.content || '', `output-${fileIndex}`)}
                title="Copy file content"
              >
                {copiedStates[`output-${fileIndex}`] ? <CheckCircle size={14} /> : <Copy size={14} />}
                <span>Copy</span>
              </button>
              <button
                className="action-button"
                onClick={() => handleDownload(output.content || '', output.filename)}
                title="Download file"
              >
                <Download size={14} />
                <span>Download</span>
              </button>
            </div>
            {renderFileContent(output)}
          </div>
        );
      }
    }

    return null;
  };

  const toggleOutputSelection = (filename) => {
    setSelectedOutputs(prev => {
      if (prev.includes(filename)) {
        return prev.filter(f => f !== filename);
      } else {
        return [...prev, filename];
      }
    });
  };

  const toggleEditExpanded = (editId) => {
    setExpandedEdits(prev => ({
      ...prev,
      [editId]: !prev[editId]
    }));
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const getSelectedOutputsContext = () => {
    if (selectedOutputs.length === 0) return '';
    return `[Context: ${selectedOutputs.join(', ')}] `;
  };

  const executeQuery = async (queryInstruction) => {
    if (!queryInstruction.trim() || !clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      return;
    }

    if (!editSessionInitialized) {
      console.error('Edit session not initialized');
      return;
    }

    // Prepend selected outputs as context
    const contextPrefix = getSelectedOutputsContext();
    const fullInstruction = contextPrefix + queryInstruction;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: fullInstruction,
      contextOutputs: selectedOutputs.length > 0 ? [...selectedOutputs] : null,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    // Clear selected outputs after sending
    setSelectedOutputs([]);
    setInstruction('');
    setIsLoading(true);

    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id,
        instruction: queryInstruction
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/edit/query?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      const agentMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.result?.response || data.response || data.error || 'No response received',
        isError: !!data.error || !data.success,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, agentMessage]);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

      // If edit was successful, reload explanation (backend updates it) and run the report
      if (data.success && !data.error) {
        loadExplanation();
        handleRunReport();
      }
    } catch (error) {
      console.error('Error executing instruction:', error);
      const errorMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: `Error: ${error.message}`,
        isError: true,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExecuteInstruction = async () => {
    await executeQuery(instruction);
  };

  const outputTabs = getOutputTabs(reportOutput?.outputs);

  return (
    <div className="report-editor-page">
      {/* Header */}
      <div className="editor-header">
        <div className="header-left">
          <button className="back-button" onClick={onClose} title="Back to reports">
            <ArrowLeft size={20} />
          </button>
          <h2 className="editor-title">Edit: {reportName}</h2>
        </div>
        <div className="header-actions">
          <button
            className="run-button"
            onClick={handleRunReport}
            title={editSessionInitialized ? "Run report" : "Initializing edit session..."}
            disabled={isRunning || !editSessionInitialized}
          >
            {isRunning || editSessionLoading ? (
              <Loader2 size={16} className="spin" />
            ) : (
              <Play size={16} />
            )}
          </button>
          <button
            className="save-button"
            onClick={handleSaveChanges}
            disabled={isSaving || !editSessionInitialized}
            title="Update report"
          >
            {isSaving ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <Save size={14} />
            )}
            <span>Update Report</span>
          </button>
        </div>
      </div>

      {/* Main Content Area - Left (Chat) / Right (Tabs) Layout */}
      <div className="editor-body">
        {/* Left Panel - Edit Commands */}
        <div className="left-panel chat-panel">
          {/* Input at top for visibility */}
          <div className="input-container input-container-top">
            {selectedOutputs.length > 0 && (
              <div className="selected-context">
                <span className="context-label">Context:</span>
                {selectedOutputs.map(filename => (
                  <span key={filename} className="context-tag">
                    {filename}
                    <button
                      className="context-remove"
                      onClick={() => toggleOutputSelection(filename)}
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="input-row">
              <textarea
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    executeQuery(instruction);
                  }
                }}
                placeholder={editSessionInitialized ? "Describe changes... (Enter to send)" : "Initializing..."}
                className="chat-input"
                disabled={isLoading || !editSessionInitialized}
                rows="2"
              />
              <button
                className="instruction-button"
                onClick={handleExecuteInstruction}
                disabled={isLoading || !instruction.trim() || !editSessionInitialized}
                title="Apply (Enter)"
              >
                {isLoading || editSessionLoading ? (
                  <Loader2 size={14} className="spin" />
                ) : (
                  <Wand2 size={14} />
                )}
              </button>
            </div>
          </div>
          <div className="chat-header">
            <h3>EDIT HISTORY</h3>
          </div>
          <div className="edits-container">
            {messages.length === 0 ? (
              <div className="edits-empty">
                <p>No edits yet</p>
                <p className="hint">Describe changes above to get started</p>
              </div>
            ) : (
              (() => {
                // Group messages into request/response pairs
                const editPairs = [];
                for (let i = 0; i < messages.length; i++) {
                  const msg = messages[i];
                  if (msg.role === 'user') {
                    const response = messages[i + 1]?.role === 'assistant' ? messages[i + 1] : null;
                    editPairs.push({ request: msg, response });
                    if (response) i++;
                  }
                }
                // Reverse to show newest first
                const reversedPairs = [...editPairs].reverse();
                const totalEdits = editPairs.length;
                return reversedPairs.map((pair, reverseIndex) => {
                  const originalIndex = totalEdits - reverseIndex; // Original numbering (newest = highest number)
                  const isExpanded = expandedEdits[pair.request.id];
                  const hasLongResponse = pair.response?.content?.length > 150;
                  return (
                    <div key={pair.request.id} className={`edit-item ${pair.response?.isError ? 'edit-error' : ''}`}>
                      <div className="edit-request">
                        <span className="edit-number">#{originalIndex}</span>
                        <span className="edit-instruction">{pair.request.content}</span>
                      </div>
                      {pair.response ? (
                        <div className="edit-response">
                          {pair.response.isError ? null : (
                            <div className="edit-success-badge">
                              <CheckCircle size={14} />
                              <span>{truncateText(pair.response.content, 80)}</span>
                            </div>
                          )}
                          <div className={`edit-response-content ${isExpanded ? 'expanded' : ''}`}>
                            {isExpanded ? (
                              <ReactMarkdown>{pair.response.content}</ReactMarkdown>
                            ) : null}
                          </div>
                          {hasLongResponse && (
                            <button
                              className="edit-expand-button"
                              onClick={() => toggleEditExpanded(pair.request.id)}
                            >
                              {isExpanded ? (
                                <><ChevronDown size={12} /> Less</>
                              ) : (
                                <><ChevronRight size={12} /> More</>
                              )}
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="edit-response edit-pending">
                          <Loader2 size={14} className="spin" />
                          <span>Processing...</span>
                        </div>
                      )}
                    </div>
                  );
                });
              })()
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Right Panel - Tabs for Explanation and Outputs */}
        <div className="right-panel tabs-panel">
          <div className="tabs-container">
            <div className="tabs">
              {/* Fixed Explanation Tab */}
              <button
                className={`tab-button ${activeTab === 'explanation' ? 'tab-button-active' : ''}`}
                onClick={() => setActiveTab('explanation')}
              >
                <Info size={16} />
                <span>Explanation</span>
              </button>
              {/* Dynamic Output Tabs */}
              {outputTabs.map((tab) => (
                <div key={tab.id} className="tab-button-wrapper">
                  <button
                    className={`tab-button ${activeTab === tab.id ? 'tab-button-active' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.output && (
                      <span
                        className={`tab-select-circle ${selectedOutputs.includes(tab.output.filename) ? 'tab-selected' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleOutputSelection(tab.output.filename);
                        }}
                        title={selectedOutputs.includes(tab.output.filename) ? `Remove ${tab.output.filename} from context` : `Add ${tab.output.filename} as context`}
                      >
                        {selectedOutputs.includes(tab.output.filename) ? <CheckCircle size={14} /> : <div className="select-circle" />}
                      </span>
                    )}
                    {tab.icon}
                    <span>{tab.label}</span>
                  </button>
                  {tab.output && (
                    <button
                      className="tab-remove-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        executeQuery(`Remove the output file "${tab.output.filename}" from this report`);
                      }}
                      title={`Remove ${tab.output.filename}`}
                      disabled={isLoading || !editSessionInitialized}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="right-panel-content">
            {renderLeftPanelContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportEditorPage;
